<template>
  <Button
    as="label"
    variant="outline"
    for="importProjectFile"
    class="hover:cursor-pointer"
    @click="onButtonClick"
  >
    <template #startIcon>
      <Upload />
    </template>
    Import
  </Button>
  <input
    v-if="isSupported"
    id="importProjectFile"
    ref="importProjectFile"
    type="file"
    class="hidden"
    accept=".arcw"
    @change="onProjectUpload"
  />
</template>

<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { Button } from '@ui/components/button';
import { Upload } from 'lucide-vue-next';
import { CancelToken, isCancel } from 'axios';
import { usePremiumFeature } from '@/composables/usePremiumFeature.js';
import emitter from 'tiny-emitter/instance';
import { EVENT } from '@/constants.js';
import requests from '@/vuex/requests.js';

const { isSupported } = usePremiumFeature({
  feature: 'import_project',
});

function onButtonClick() {
  if (!isSupported.value) {
    // TODO: Add message
    emitter.emit(EVENT.LIMIT_MODAL, {
      open: true,
      title: 'The import project feature is not available to your current plan',
      subtitle: 'Upgrade to any of our plans to unlock import project feature',
    });
  }
}

const store = useStore();

const uploadingProjects = ref([]);
const importProjectFile = ref(null);
const importErrors = ref([]);

function onProjectUpload() {
  window.onbeforeunload = () =>
    "You're still uploading. Are you sure you want to leave?";
  const source = CancelToken.source();

  requests.projects
    .generateUploadUrl(store.getters['workspace/getId'])
    .then(urlResponse => {
      const count = uploadingProjects.value.push({
        hash: urlResponse.data.projectHash,
        cancel: source.cancel,
      });
      const index = count - 1;
      uploadingProjects.value[index].progressCallback = progressEvent => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );

        uploadingProjects.value[index].progress = percentCompleted;
      };

      requests.projects
        .uploadProject({
          projectFile: importProjectFile.value?.files[0],
          urlInfo: urlResponse.data,
          progressCallback: uploadingProjects.value[index].progressCallback,
          cancelToken: source.token,
        })
        .then(
          () => {
            window.onbeforeunload = null;
            importErrors.value = [];
            emitter.on(EVENT.PROJECT_IMPORT_COMPLETED, obj => {
              if (obj.hash === urlResponse.data.projectHash)
                uploadingProjects.value.splice(index, 1);
            });
          },
          err => {
            window.onbeforeunload = null;
            if (isCancel(err)) {
              store.dispatch['user/projects/deleteProject']({
                hash: urlResponse.data.projectHash,
              });
              uploadingProjects.value.splice(index, 1);
            } else {
              importErrors.value = err.response.data.errors;
              emitter.on(EVENT.PROJECT_IMPORT_COMPLETED, obj => {
                if (obj.hash === urlResponse.data.projectHash)
                  uploadingProjects.value.splice(index, 1);
              });
              store.dispatch['application/modals/setProjectImportErrorModal']({
                value: true,
              });
            }
          }
        );
    });
}
</script>
